import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Grid,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Phone as PhoneIcon,
  CallEnd as CallEndIcon,
  SmartToy as SmartToyIcon,
  Person as PersonIcon,
  SyncOutlined as SyncIcon
} from '@mui/icons-material';
import Vapi from '@vapi-ai/web';
import { db, doc, getDoc, setDoc, serverTimestamp } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';

const VAPI_PUBLIC_KEY = 'df56edbd-2cfa-42bf-b183-93517226da94';
const ASSISTANT_ID = '4d417f4c-a8e9-4784-9d30-91fd4396dad8';

interface VoiceOption {
  id: string;
  label: string;
  provider: string;
  voiceId: string;
  model?: string;
  gender: 'Female' | 'Male';
}

const VOICE_OPTIONS: VoiceOption[] = [
  { id: 'default', label: 'Current (11labs)', provider: '11labs', voiceId: 'Ky9j3wxFbp3dSAdrkOEv', model: 'eleven_turbo_v2_5', gender: 'Female' },
  { id: 'sarah', label: 'Sarah — Soft, warm', provider: '11labs', voiceId: 'EXAVITQu4vr4xnSDxMaL', gender: 'Female' },
  { id: 'rachel', label: 'Rachel — Calm, clear', provider: '11labs', voiceId: '21m00Tcm4TlvDq8ikWAM', gender: 'Female' },
  { id: 'charlotte', label: 'Charlotte — Friendly, upbeat', provider: '11labs', voiceId: 'XB0fDUnXU5powFXDhCwa', gender: 'Female' },
  { id: 'lily', label: 'Lily — Bright, youthful', provider: '11labs', voiceId: 'pFZP5JQG7iQjIQuC4Bku', gender: 'Female' },
  { id: 'matilda', label: 'Matilda — Warm, natural', provider: '11labs', voiceId: 'XrExE9yKIg1WjnnlVkGX', gender: 'Female' },
  { id: 'grace', label: 'Grace — Professional', provider: '11labs', voiceId: 'oWAxZDx7w5VEj9dCyTzz', gender: 'Female' },
  { id: 'brian', label: 'Brian — Deep, friendly', provider: '11labs', voiceId: 'nPczCjzI2devNBz1zQrb', gender: 'Male' },
  { id: 'george', label: 'George — Warm, British', provider: '11labs', voiceId: 'JBFqnCBsd6RMkjVDRZzb', gender: 'Male' },
  { id: 'josh', label: 'Josh — Conversational', provider: '11labs', voiceId: 'TxGEqnHWrfWFTfGW9XjX', gender: 'Male' },
  { id: 'daniel', label: 'Daniel — Clear, authoritative', provider: '11labs', voiceId: 'onwK4e9ZLuTAKqWW03F9', gender: 'Male' },
  { id: 'chris', label: 'Chris — Casual, natural', provider: '11labs', voiceId: 'iP95p4xoKVk53GoZ742B', gender: 'Male' },
  { id: 'liam', label: 'Liam — Energetic', provider: '11labs', voiceId: 'TX3LPaxmHKxFdv7VOQHJ', gender: 'Male' },
];

interface AssistantInfo {
  name: string;
  model: string;
  voiceProvider: string;
  toolCount: number;
  updatedAt: string;
}

interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
}

type CallState = 'idle' | 'connecting' | 'in-call';

const VoiceAIPage: React.FC = () => {
  const { user } = useAuth();
  const [assistantInfo, setAssistantInfo] = useState<AssistantInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [selectedVoice, setSelectedVoice] = useState<string>('default');
  const [callState, setCallState] = useState<CallState>('idle');
  const [callError, setCallError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const vapiRef = useRef<Vapi | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Greeting script state
  const [greeting, setGreeting] = useState<string>('');
  const [savedGreeting, setSavedGreeting] = useState<string>('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch assistant info from Vapi API
  useEffect(() => {
    const fetchAssistantInfo = async () => {
      try {
        const resp = await fetch(`https://api.vapi.ai/assistant/${ASSISTANT_ID}`, {
          headers: { 'Authorization': `Bearer ${VAPI_PUBLIC_KEY}` }
        });
        if (!resp.ok) throw new Error(`Failed to fetch assistant info`);
        const data = await resp.json();
        setAssistantInfo({
          name: data.name || 'Unknown',
          model: data.model?.model || 'Unknown',
          voiceProvider: data.voice?.provider || 'Unknown',
          toolCount: data.model?.tools?.length || 0,
          updatedAt: data.updatedAt || data.createdAt || ''
        });
      } catch (err: any) {
        setInfoError('Could not load assistant details. The call button still works.');
      } finally {
        setInfoLoading(false);
      }
    };
    fetchAssistantInfo();
  }, []);

  // Load greeting from Firestore on mount
  useEffect(() => {
    const loadGreeting = async () => {
      try {
        const snap = await getDoc(doc(db, 'voiceSettings', 'assistantConfig'));
        if (snap.exists()) {
          const script = snap.data().greetingScript ?? '';
          setGreeting(script);
          setSavedGreeting(script);
        }
      } catch (err) {
        console.error('Failed to load greeting:', err);
      }
    };
    loadGreeting();
  }, []);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (vapiRef.current) {
        vapiRef.current.stop();
        vapiRef.current = null;
      }
    };
  }, []);

  const startTimer = useCallback(() => {
    setDuration(0);
    timerRef.current = setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleSaveAndSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      await setDoc(doc(db, 'voiceSettings', 'assistantConfig'), {
        greetingScript: greeting.trim(),
        updatedAt: serverTimestamp(),
        updatedBy: user?.email ?? 'admin'
      }, { merge: true });
      setSavedGreeting(greeting.trim());
    } catch (err) {
      console.error('Failed to save greeting:', err);
      setSyncResult({ success: false, message: err instanceof Error ? err.message : 'Failed to save' });
      setSyncing(false);
      return;
    }
    try {
      const response = await apiService.syncVoiceGreeting();
      const data = await response.json();
      if (data.success) {
        setSyncResult({ success: true, message: 'Greeting saved and live on next call' });
      } else {
        setSyncResult({ success: false, message: data.error || 'Saved to Firestore but Vapi sync failed' });
      }
    } catch (err) {
      console.error('Failed to sync greeting:', err);
      setSyncResult({ success: false, message: 'Saved to Firestore but Vapi sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleStartCall = async () => {
    try {
      setCallError(null);
      setTranscript([]);
      setCallState('connecting');

      const vapi = new Vapi(VAPI_PUBLIC_KEY);
      vapiRef.current = vapi;

      vapi.on('call-start', () => {
        setCallState('in-call');
        startTimer();
      });

      vapi.on('call-end', () => {
        setCallState('idle');
        stopTimer();
        vapiRef.current = null;
      });

      vapi.on('message', (msg: any) => {
        if (msg.type === 'transcript' && msg.transcriptType === 'final') {
          setTranscript(prev => [...prev, { role: msg.role, text: msg.transcript }]);
        }
      });

      vapi.on('error', (err: any) => {
        console.error('Vapi error:', err);
        setCallError(err?.message || 'An error occurred during the call');
        setCallState('idle');
        stopTimer();
        vapiRef.current = null;
      });

      const voice = VOICE_OPTIONS.find(v => v.id === selectedVoice) || VOICE_OPTIONS[0];
      const overrides: any = {
        voice: {
          provider: voice.provider,
          voiceId: voice.voiceId,
          ...(voice.model ? { model: voice.model } : {})
        }
      };
      await vapi.start(ASSISTANT_ID, overrides);
    } catch (err: any) {
      console.error('Failed to start call:', err);
      setCallError(err?.message || 'Failed to start call. Check microphone permissions.');
      setCallState('idle');
      stopTimer();
      vapiRef.current = null;
    }
  };

  const handleEndCall = () => {
    if (vapiRef.current) {
      vapiRef.current.stop();
    }
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-NZ', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const callStateColor = (state: CallState) => {
    switch (state) {
      case 'idle': return 'default';
      case 'connecting': return 'warning';
      case 'in-call': return 'success';
    }
  };

  const callStateLabel = (state: CallState) => {
    switch (state) {
      case 'idle': return 'Idle';
      case 'connecting': return 'Connecting...';
      case 'in-call': return 'In Call';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>Voice AI Assistant</Typography>
        <Typography variant="body1" color="text.secondary">
          Test and manage the Adrift voice AI assistant powered by Vapi.
        </Typography>
      </Box>

      {callError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setCallError(null)}>
          {callError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Greeting Script Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Greeting Script
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The first thing the voice AI says when a call connects.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={greeting}
                onChange={(e) => {
                  setGreeting(e.target.value);
                  setSyncResult(null);
                }}
                placeholder="Kia ora! Thanks for calling AdriftNZ..."
                helperText={`${greeting.length} characters${greeting.length > 500 ? ' — long greetings may feel slow for callers' : ''}`}
                inputProps={{ maxLength: 1000 }}
              />
              <Box display="flex" gap={2} mt={2} alignItems="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveAndSync}
                  disabled={syncing || !greeting.trim()}
                  startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                >
                  {syncing ? 'Saving...' : 'Save & Push Live'}
                </Button>
                {syncResult && (
                  <Alert
                    severity={syncResult.success ? 'success' : 'error'}
                    onClose={() => setSyncResult(null)}
                    sx={{ py: 0, flex: 1 }}
                  >
                    {syncResult.message}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Assistant Status</Typography>
              {infoLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Box>
              ) : infoError ? (
                <Alert severity="warning" sx={{ mt: 1 }}>{infoError}</Alert>
              ) : assistantInfo && (
                <Box>
                  <InfoRow label="Name" value={assistantInfo.name} />
                  <InfoRow label="Model" value={assistantInfo.model} />
                  <InfoRow label="Voice Provider" value={assistantInfo.voiceProvider} />
                  <InfoRow label="Tools" value={String(assistantInfo.toolCount)} />
                  <InfoRow label="Last Updated" value={formatDate(assistantInfo.updatedAt)} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Test Call Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Test Call</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={callStateLabel(callState)}
                    color={callStateColor(callState)}
                    size="small"
                  />
                  {callState === 'in-call' && (
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                      {formatDuration(duration)}
                    </Typography>
                  )}
                </Box>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={callState !== 'idle'}>
                <InputLabel>Voice</InputLabel>
                <Select
                  value={selectedVoice}
                  label="Voice"
                  onChange={(e) => setSelectedVoice(e.target.value)}
                >
                  {VOICE_OPTIONS.filter(v => v.gender === 'Female').length > 0 && (
                    <MenuItem disabled sx={{ fontSize: '0.75rem', fontWeight: 'bold', opacity: '1 !important', color: 'text.secondary' }}>
                      Female Voices
                    </MenuItem>
                  )}
                  {VOICE_OPTIONS.filter(v => v.gender === 'Female').map(v => (
                    <MenuItem key={v.id} value={v.id} sx={{ pl: 3 }}>{v.label}</MenuItem>
                  ))}
                  {VOICE_OPTIONS.filter(v => v.gender === 'Male').length > 0 && (
                    <MenuItem disabled sx={{ fontSize: '0.75rem', fontWeight: 'bold', opacity: '1 !important', color: 'text.secondary' }}>
                      Male Voices
                    </MenuItem>
                  )}
                  {VOICE_OPTIONS.filter(v => v.gender === 'Male').map(v => (
                    <MenuItem key={v.id} value={v.id} sx={{ pl: 3 }}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" justifyContent="center" my={3}>
                {callState === 'idle' ? (
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<PhoneIcon />}
                    onClick={handleStartCall}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Start Call
                  </Button>
                ) : callState === 'connecting' ? (
                  <Button
                    variant="contained"
                    color="warning"
                    size="large"
                    disabled
                    sx={{ px: 4, py: 1.5 }}
                  >
                    <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                    Connecting...
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<CallEndIcon />}
                    onClick={handleEndCall}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    End Call
                  </Button>
                )}
              </Box>

              {/* Transcript */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 280,
                  overflowY: 'auto',
                  bgcolor: 'grey.50',
                  minHeight: 100
                }}
              >
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Transcript
                </Typography>
                {transcript.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {callState === 'idle'
                      ? 'Start a call to see the transcript here.'
                      : 'Waiting for speech...'}
                  </Typography>
                ) : (
                  transcript.map((entry, i) => (
                    <Box key={i} display="flex" alignItems="flex-start" gap={1} mb={1}>
                      {entry.role === 'assistant' ? (
                        <SmartToyIcon fontSize="small" color="primary" sx={{ mt: 0.3 }} />
                      ) : (
                        <PersonIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                      )}
                      <Typography variant="body2">{entry.text}</Typography>
                    </Box>
                  ))
                )}
                <div ref={transcriptEndRef} />
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Info Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Reference</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <InfoRow label="Assistant ID" value={ASSISTANT_ID} mono />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InfoRow label="Webhook URL" value="Firebase Cloud Function" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Vapi Dashboard</Typography>
                  <Typography variant="body2">
                    <a
                      href="https://dashboard.vapi.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2' }}
                    >
                      dashboard.vapi.ai
                    </a>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Box mb={1.5}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : undefined}>
      {value}
    </Typography>
  </Box>
);

export default VoiceAIPage;
